import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Form, Input, List, Popconfirm, Select, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { countryApi, languageApi, sankalpaLineBhedApi } from '../services/api';
import { Country, Language, SankalpaLineBhed } from '../types';

const { Text } = Typography;
const { Option } = Select;

interface SankalpaLineBhedPanelProps {
  sankalpaLineId: number | null;
  visible: boolean;
  lineContent?: string;
  onBhedsChange?: () => void;
  initialEditBhedId?: number | null;
}

const SankalpaLineBhedPanel: React.FC<SankalpaLineBhedPanelProps> = ({
  sankalpaLineId,
  visible,
  lineContent = '',
  onBhedsChange,
  initialEditBhedId,
}) => {
  const [bhedForm] = Form.useForm();
  const [bheds, setBheds] = useState<SankalpaLineBhed[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingBhedId, setEditingBhedId] = useState<number | null>(null);

  const languageNameById = useMemo(() => {
    const map = new Map<number, string>();
    languages.forEach((language) => {
      if (language.id != null) map.set(language.id, language.name);
    });
    return map;
  }, [languages]);

  const countryNameById = useMemo(() => {
    const map = new Map<number, string>();
    countries.forEach((country) => {
      if (country.id != null) map.set(country.id, country.name);
    });
    return map;
  }, [countries]);

  const loadReferenceData = useCallback(async () => {
    const [languagesResp, countriesResp] = await Promise.all([
      languageApi.getAll(),
      countryApi.getAll(),
    ]);
    setLanguages(languagesResp.data);
    setCountries(countriesResp.data);
  }, []);

  const loadBheds = useCallback(async () => {
    if (!sankalpaLineId) {
      setBheds([]);
      return;
    }
    setLoading(true);
    try {
      const response = await sankalpaLineBhedApi.getBySankalpaLineId(sankalpaLineId);
      setBheds(response.data);
    } catch (error) {
      console.error('Error loading bheds:', error);
      message.error('Failed to load bheds');
    } finally {
      setLoading(false);
    }
  }, [sankalpaLineId]);

  const selectedCountryId = Form.useWatch('countryId', bhedForm);
  const selectedLanguageId = Form.useWatch('languageId', bhedForm);
  const isEditing = editingBhedId != null;
  const showLanguage = isEditing || selectedCountryId != null;
  const showText = isEditing || (selectedCountryId != null && selectedLanguageId != null);

  const prefillAddBhedText = useCallback(() => {
    bhedForm.setFieldsValue({ text: lineContent });
  }, [bhedForm, lineContent]);

  useEffect(() => {
    if (!visible) {
      setEditingBhedId(null);
      bhedForm.resetFields();
      return;
    }

    loadReferenceData().catch((error) => {
      console.error('Error loading bhed reference data:', error);
      message.error('Failed to load languages or countries');
    });
    loadBheds();
    setEditingBhedId(null);
    bhedForm.resetFields();
    prefillAddBhedText();
  }, [visible, sankalpaLineId, bhedForm, loadBheds, loadReferenceData, prefillAddBhedText]);

  const resetBhedForm = () => {
    setEditingBhedId(null);
    bhedForm.resetFields();
    prefillAddBhedText();
  };

  const handleSaveBhed = async (values: {
    text: string;
    languageId: number;
    countryId: number;
  }) => {
    if (!sankalpaLineId) return;

    setSaving(true);
    try {
      const payload = {
        text: values.text.trim(),
        languageId: Number(values.languageId),
        countryId: Number(values.countryId),
      };

      if (editingBhedId) {
        await sankalpaLineBhedApi.update(editingBhedId, payload);
        message.success('Bhed updated successfully');
      } else {
        await sankalpaLineBhedApi.create({
          sankalpaLineId,
          ...payload,
        });
        message.success('Bhed created successfully');
      }

      resetBhedForm();
      await loadBheds();
      onBhedsChange?.();
    } catch (error: any) {
      console.error('Error saving bhed:', error);
      const data = error?.response?.data;
      const msg = Array.isArray(data?.message)
        ? data.message.join(', ')
        : data?.message || error?.message;
      message.error(msg || 'Failed to save bhed');
    } finally {
      setSaving(false);
    }
  };

  const handleEditBhed = useCallback((bhed: SankalpaLineBhed) => {
    setEditingBhedId(bhed.id);
    bhedForm.setFieldsValue({
      text: bhed.text,
      languageId: bhed.languageId,
      countryId: bhed.countryId,
    });
  }, [bhedForm]);

  useEffect(() => {
    if (!visible || initialEditBhedId == null || bheds.length === 0) return;
    const bhed = bheds.find((item) => item.id === initialEditBhedId);
    if (bhed) handleEditBhed(bhed);
  }, [visible, initialEditBhedId, bheds, handleEditBhed]);

  const handleDeleteBhed = async (bhedId: number) => {
    try {
      await sankalpaLineBhedApi.delete(bhedId);
      message.success('Bhed deleted successfully');
      if (editingBhedId === bhedId) {
        resetBhedForm();
      }
      await loadBheds();
      onBhedsChange?.();
    } catch (error) {
      console.error('Error deleting bhed:', error);
      message.error('Failed to delete bhed');
    }
  };

  const getLanguageLabel = (bhed: SankalpaLineBhed) =>
    bhed.language?.name || languageNameById.get(bhed.languageId) || `Language ${bhed.languageId}`;

  const getCountryLabel = (bhed: SankalpaLineBhed) =>
    bhed.country?.name || countryNameById.get(bhed.countryId) || `Country ${bhed.countryId}`;

  return (
    <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 16 }}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Bhed
      </Typography.Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Each sankalpa line can have multiple bhed entries with text, language, and country.
      </Text>

      <List
        loading={loading}
        locale={{ emptyText: 'No bhed added yet' }}
        dataSource={bheds}
        renderItem={(bhed) => (
          <List.Item
            actions={[
              <Button
                key="edit"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditBhed(bhed)}
              />,
              <Popconfirm
                key="delete"
                title="Delete this bhed?"
                onConfirm={() => handleDeleteBhed(bhed.id)}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              title={
                <span>
                  {getLanguageLabel(bhed)} · {getCountryLabel(bhed)}
                </span>
              }
              description={<span style={{ whiteSpace: 'pre-wrap' }}>{bhed.text}</span>}
            />
          </List.Item>
        )}
      />

      <Divider style={{ margin: '16px 0' }} />
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {editingBhedId ? 'Edit Bhed' : 'Add Bhed'}
      </Typography.Title>

      <Form form={bhedForm} layout="vertical" onFinish={handleSaveBhed}>
        <Form.Item
          name="countryId"
          label="Country"
          rules={[{ required: true, message: 'Please select a country' }]}
        >
          <Select
            placeholder="Select country"
            showSearch
            optionFilterProp="children"
            onChange={() => {
              if (!editingBhedId) {
                bhedForm.setFieldsValue({ languageId: undefined });
              }
            }}
          >
            {countries.map((country) => (
              <Option key={country.id} value={country.id}>
                {country.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {showLanguage && (
          <Form.Item
            name="languageId"
            label="Language"
            rules={[{ required: true, message: 'Please select a language' }]}
          >
            <Select placeholder="Select language" showSearch optionFilterProp="children">
              {languages.map((language) => (
                <Option key={language.id} value={language.id}>
                  {language.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {showText && (
          <Form.Item
            name="text"
            label="Text"
            rules={[{ required: true, message: 'Please enter bhed text' }]}
          >
            <Input.TextArea rows={3} placeholder="Enter bhed text" />
          </Form.Item>
        )}

        {showText && (
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={saving} icon={<PlusOutlined />}>
              {editingBhedId ? 'Update Bhed' : 'Add Bhed'}
            </Button>
            {editingBhedId && (
              <Button style={{ marginLeft: 8 }} onClick={resetBhedForm}>
                Cancel Edit
              </Button>
            )}
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default SankalpaLineBhedPanel;
